import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOptionsGlobal } from 'actions/global';
import { capitalizedString, mapDataOptions } from 'utils/helpers';
import isEqual from 'lodash/isEqual';
import { createRequestKey } from 'utils/dedupeRequest';
import { subscribeSupplierOptionsRefresh } from 'utils/supplierOptionsRefresh';

function useGetOptions(optionName, funcOptions = {}) {
  const dispatch = useDispatch();
  const {
    valueName = 'id',
    labelName = 'name',
    valueAsString = false,
    params = { is_active: 1 },
    checkParams = true,
    isHasOptionAll = false,
  } = funcOptions;

  const storedOptions = useSelector((state) => state.global[`${optionName}Data`]);
  const storedParams = useSelector((state) => state.global[`${optionName}Params`]);
  const isFetching = useSelector((state) => state.global[`get${capitalizedString(optionName)}Loading`]);
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const supplierParamsKey = optionName === 'supplier' ? createRequestKey('supplier', params) : null;

  useEffect(() => {
    if (optionName !== 'supplier' || !checkParams) return;
    const refresh = () => dispatch(getOptionsGlobal('supplier', paramsRef.current)).catch(() => {});
    const unsubscribe = subscribeSupplierOptionsRefresh(refresh);
    refresh();
    return unsubscribe;
  }, [dispatch, optionName, checkParams, supplierParamsKey]);

  useEffect(() => {
    if (optionName === 'supplier' || isFetching || !checkParams) return;
    if (!storedOptions || !isEqual(storedParams, params)) {
      dispatch(getOptionsGlobal(optionName, params)).catch(() => {});
    }
  }, [params, checkParams, isFetching, dispatch, optionName]);

  const options = useMemo(() => {
    const sourceOptions = Array.isArray(storedOptions) ? storedOptions : [];
    const nextOptions = isHasOptionAll ? [{ id: 0, name: 'Tất cả' }, ...sourceOptions] : sourceOptions;

    return mapDataOptions(nextOptions, { valueName, labelName, valueAsString });
  }, [storedOptions, isHasOptionAll, valueName, labelName, valueAsString]);

  return options?.length ? options : [];
}

/**
 * this list ref to: api-portal/app/modules/global/utils/constants.js
 */
export const optionType = {
  supplier: 'supplier',
  purchaseRequisition: 'purchaseRequisition',
  store: 'store',
  company: 'company',
  department: 'department',
  purchaseOrder: 'purchaseOrder',
  business: 'business',
  area: 'area',
  taskWorkFlow: 'taskWorkFlow',
  productModel: 'productModel',
  conditionDb: 'conditionDb',
  source: 'source',
  customerType: 'customerType',
  poReviewLevel: 'poReviewLevel',
  user: 'user',
  discountProgram: 'discountProgram',
  poDivisionReviewLevel: 'poDivisionReviewLevel',
  taskType: 'taskType',
  taskTypeAuto: 'taskTypeAuto',
  order: 'order',
  stocksInType: 'stocksInType',
  stocksCompany: 'stocksCompany',
  bank: 'bank',
  province: 'province',
  block: 'block',
  hobbiesForUser: 'hobbiesForUser',
  cluster: 'cluster',
  zaloTemplate: 'zaloTemplate',
  expendType: 'expendType',
  purchaseRequisitionType: 'purchaseRequisitionType',
  storeType: 'storeType',
  brand: 'brand',
  taskTypeWflow: 'taskTypeWflow',
  requestPurchaseOrder: 'requestPurchaseOrder',
  positionByDepartment: 'positionByDepartment',
  interestContent: 'interestContent',
  stocksType: 'stocksType',
  documentType: 'documentType',
  timeKeepingClaimType: 'timeKeepingClaimType',
  shift: 'shift',
  accounting_account: 'accounting_account',
  accountingAccount: 'accounting_account',
  internalTransferType: 'internalTransferType',
  storeByBusiness: 'storeByBusiness',
  stocks: 'stocks',
  stockTypeByStore: 'stockTypeByStore',
  paymentForm: 'paymentForm',
};

export default useGetOptions;
